import React, { useEffect, useState } from "react";
import type { NextPage } from "next";
import Layout from "../layout/layout";
import Image from "next/image";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { phoneRegExp } from "@/utils/regex";
import httpClient from "@/api/http-client";
import endpoints from "@/api/endpoints";
import banner from "../assets/images/banner-main.jpg";
import { useSelector } from "react-redux";
import { authActions, selectAuth } from "@/store/slices/auth";
import { useAppDispatch, wrapper } from "@/store";
import { useRouter } from "next/router";
import Spinner from "react-bootstrap/Spinner";
import { unbxdCusEngTrackEvent, unbxdCusEngTrackUser } from "@/lib/unbxd";

import styles from "../styles/bookanappointment.module.scss";
import { setPath, setTitle, setMeta } from "@/store/slices/helmet";
import moment from "moment-timezone";
const Bookanappointment: NextPage = () => {
  const router = useRouter();
  const authState = useSelector(selectAuth);
  const dispatch = useAppDispatch();

  const [loading, setLoading] = useState(false);
  const [loadings, setLoadings] = useState(false);

  const [states, setStates] = useState<string[]>([]);
  const [cities, setCities] = useState<string[]>([]);
  const [stores, setStores] = useState<any[]>([]);
  const [store, setStore] = useState<any>({});
  const [tncUrl, setTncUrl] = useState<string>("");
  const [subPurpose, setSubPurpose] = useState<any[]>([]);
  const [subPurposeTitle, setSubPurposeTitle] = useState<string>("");

  function getNextDate() {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split("T")[0];
  }

  const formik = useFormik({
    initialValues: {
      state: "",
      city: "",
      store_id: "",
      appointment_date: getNextDate(),
      appointment_time: "",
      appointment_purpose: "",
      sub_purpose: "",
      email: "",
      accept_tc: false,
    },
    validationSchema: Yup.object({
      city: Yup.string().required("City is required"),
      store_id: Yup.string().required("Store is required"),
      state: Yup.string().required("State is required"),
      appointment_date: Yup.date()
        .min(new Date(), "Please select a future date")
        .required("Appointment date is required"),
      appointment_time: Yup.string().required("Appointment time is required"),
      appointment_purpose: Yup.string().required(
        "Appointment purpose is required"
      ),
      email: Yup.string()
        .email("Invalid email address")
        .required("Email is required"),
    }),
    onSubmit: async (values) => {
      if (loadings) return;
      if (!authState.user) {
        dispatch(authActions.setIsModalOpen(true));
        return;
      }
      if (!authState.user.billing_address) {
        dispatch(authActions.setBillingModalOpen(true));
        return;
      }
      if (tncUrl.length > 0 && !values.accept_tc) {
        toast("Please accept the terms and conditions.", {
          type: "warning",
        });
        return;
      }
      if (subPurpose.length > 0 && !values.sub_purpose) {
        toast("Please specify the sub-purpose.", {
          type: "warning",
        });
        return;
      }
      if (!authState.user.email && !values.email) {
        toast("Please enter email.", {
          type: "warning",
        });
        return;
      }
      setLoadings(true);
      try {
        const { data } = await httpClient.post(
          endpoints.store_appointment.create(),
          {
            store_id: values.store_id,
            appointment_date: values.appointment_date,
            appointment_time: values.appointment_time,
            appointment_purpose: values.appointment_purpose,
            sub_purpose: values.sub_purpose,
            email: values.email,
          }
        );
        if (data.status === true) {
          toast("Store Appointment Booked.", {
            type: "success",
          });
          if (window.smartech) {
            unbxdCusEngTrackEvent("store_appointment_booked", {
              date: moment().format("DD-MM-YYYY"),
            });
            window.smartech(
              "identify",
              `${process.env.NEXT_PUBLIC_MOB_PREFIX}${authState.user?.mobile}`
            );
            
            unbxdCusEngTrackUser({
              "pk^mobile": `${process.env.NEXT_PUBLIC_MOB_PREFIX}${authState.user?.mobile}`,
              email: values.email,
              NAME: authState.user?.name,
              GENDER: authState.user?.gender,
              SENCO_USER: "yes",
            });
          }

          setLoadings(false);
        }
        const basePath = process.env.NEXT_PUBLIC_BASE_PATH
          ? process.env.NEXT_PUBLIC_BASE_PATH + "/account/store-appointment"
          : "/account/store-appointment";
        window.location.href = basePath;
      } catch (error: any) {
        toast(error?.response?.data?.message || "Something Went wrong", {
          type: "warning",
        });
        setLoadings(false);
      }
    },
  });

  const loadStates = async () => {
    try {
      setLoading(true);
      const { data } = await httpClient.get(endpoints.stores.states(), {
        params: {
          appointment: "yes",
        },
      });
      if (data) {
        setStates(data.data);
        setLoading(false);
      }
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  const loadCities = async (state: string) => {
    try {
      setLoading(true);
      const { data } = await httpClient.get(endpoints.stores.cities(state), {
        params: {
          appointment: "yes",
        },
      });
      if (data) {
        setCities(data.data);
        setLoading(false);
      }
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  const loadStores = async (state: string, city: string) => {
    try {
      setLoading(true);
      const { data } = await httpClient.get(
        endpoints.stores.stores(state, city),
        {
          params: {
            appointment: "yes",
          },
        }
      );
      if (data) {
        setStores(data.data);
        setLoading(false);
      }
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  const loadStore = async (store_code: string) => {
    try {
      setLoading(true);
      const { data } = await httpClient.get(
        endpoints.stores.store(store_code),
        {
          params: {
            appointment: "yes",
          },
        }
      );
      if (data) {
        setStore(data.data);
        setLoading(false);
      }
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  const handleLoginClick = async () => {
    if (!authState.user) {
      dispatch(authActions.setIsModalOpen(true));
      return;
    }
  };

  useEffect(() => {
    loadStates();
  }, []);

  const handleChange = (name: string, value: any) => {
    formik.setFieldValue(name, value);
    if (
      authState &&
      authState.user &&
      authState.user.name &&
      !formik.values.email
    ) {
      formik.setFieldValue("email", authState.user.email);
    }
    if (name === "state" && value) {
      loadCities(value);
      setCities([]);
      setStores([]);
      setStore({});
    } else if (name === "city" && value) {
      loadStores(formik.values.state, value);
      setStores([]);
      setStore({});
    } else if (name === "store_id" && value) {
      loadStore(value);
      setStore({});
    } else if (name === "appointment_purpose" && value) {
      if (store && store.purposes && store.purposes.length > 0) {
        let chield = store.purposes.filter((data: any) => data.id == value);
        if (chield && chield.length > 0) {
          if (chield[0].tnc) {
            setTncUrl(chield[0].tnc_page);
          } else {
            setTncUrl("");
          }
          setSubPurpose(chield[0].children);
          setSubPurposeTitle(chield[0].title);
        }
      }
    }
  };

  return (
    <Layout>
      <div className={styles.silver_booking_banner}>
        <Image src={banner} alt="" />
      </div>

      <form>
        {loading && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(255, 255, 255, 0.5)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 9999,
            }}
          >
            <Spinner animation="grow" role="status"></Spinner>
          </div>
        )}
        <div className={styles.silver_booking_main}>
          <div className={styles.bg}>
            <div className="container">
              <div className={styles.silver_booking_bg}>
                <form>
                  <div className="row">
                    <div className="col-md-4 pb-2">
                      <label>Select State</label>
                      <select
                        className={styles.custom_select}
                        id="state"
                        name="state"
                        onChange={(e) => handleChange("state", e.target.value)}
                        value={formik.values.state}
                      >
                        {states.length > 0 && (
                          <>
                            <option value="">--select your state--</option>
                            {states.map((value, index) => {
                              return (
                                <option key={index} value={value}>
                                  {value}
                                </option>
                              );
                            })}
                          </>
                        )}
                      </select>
                      {formik.touched.state && formik.errors.state ? (
                        <small className="text-danger">
                          {formik.errors.state}
                        </small>
                      ) : null}
                    </div>
                    <div className="col-md-4 pb-2">
                      <label>Select City</label>
                      <select
                        className={styles.custom_select}
                        name="city"
                        id="cities"
                        onChange={(e) => handleChange("city", e.target.value)}
                        value={formik.values.city}
                      >
                        {cities.length > 0 && (
                          <>
                            <option value="">--select your city--</option>
                            {cities.map((value, index) => {
                              return (
                                <option key={index} value={value}>
                                  {value}
                                </option>
                              );
                            })}
                          </>
                        )}
                      </select>
                      {formik.touched.city && formik.errors.city ? (
                        <small className="text-danger">
                          {formik.errors.city}
                        </small>
                      ) : null}
                    </div>
                    <div className="col-md-4 pb-2">
                      <label>Select Store</label>
                      <select
                        className={styles.custom_select}
                        name="store_id"
                        onChange={(e) =>
                          handleChange("store_id", e.target.value)
                        }
                        value={formik.values.store_id}
                      >
                        {stores.length > 0 && (
                          <>
                            <option value="">--select your store--</option>
                            {stores.map((value, index) => {
                              return (
                                <option key={index} value={value.store_code}>
                                  {value.store_name}
                                </option>
                              );
                            })}
                          </>
                        )}
                      </select>
                      {formik.touched.store_id && formik.errors.store_id ? (
                        <small className="text-danger">
                          {formik.errors.store_id}
                        </small>
                      ) : null}
                    </div>
                  </div>
                  {store && Object.values(store).length > 0 && (
                    <div className="row">
                      <div className="col-md-12">
                        <div className={styles.head}>Store Details</div>
                        <div className="border rounded p-3 bg-white w-100 mb-3">
                          <div className="row">
                            <div className="col-md-6">
                              <p className={styles.text_p}>
                                <span>Name : </span>
                                <span> {store.store_name} </span>
                              </p>
                            </div>
                            <div className="col-md-6">
                              <p className={styles.text_p}>
                                <span>Code : </span>
                                <span> {store.store_code} </span>
                              </p>
                            </div>
                            <div className="col-md-6">
                              <p className={styles.text_p}>
                                <span>State : </span>
                                <span> {store.store_state} </span>
                              </p>
                            </div>
                            <div className="col-md-6">
                              <p className={styles.text_p}>
                                <span>City : </span>
                                <span> {store.store_city} </span>
                              </p>
                            </div>
                            <div className="col-md-6">
                              <p className={styles.text_p}>
                                <span>Pincode : </span>
                                <span> {store.store_pin} </span>
                              </p>
                            </div>
                            <div className="col-md-6">
                              <p className={styles.text_p}>
                                <span>Address : </span>
                                <span> {store.store_address} </span>
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-12">
                        <div className="row">
                          <div className="col-md-4">
                            <div className={styles.form_group}>
                              <label htmlFor="exampleInputEmail1">
                                Choose Date
                              </label>
                              <input
                                type="date"
                                className={styles.form_control}
                                {...formik.getFieldProps("appointment_date")}
                                min={getNextDate()}
                              />
                              {formik.touched.appointment_date &&
                              formik.errors.appointment_date ? (
                                <small className="text-danger">
                                  {formik.errors.appointment_date}
                                </small>
                              ) : null}
                            </div>
                          </div>
                          <div className="col-md-4">
                            <div className={styles.form_group}>
                              <label htmlFor="exampleInputEmail1">
                                Select time
                              </label>
                              <select
                                className={styles.custom_select}
                                {...formik.getFieldProps("appointment_time")}
                              >
                                {store &&
                                  store.timings &&
                                  store.timings.length > 0 && (
                                    <>
                                      <option value="" selected>
                                        ----Select Time----
                                      </option>
                                      {store.timings.map(
                                        (value: any, index: number) => {
                                          return (
                                            <option
                                              key={index}
                                              value={value.id}
                                            >
                                              {value.time}
                                            </option>
                                          );
                                        }
                                      )}
                                    </>
                                  )}
                              </select>
                              {formik.touched.appointment_time &&
                              formik.errors.appointment_time ? (
                                <small className="text-danger">
                                  {formik.errors.appointment_time}
                                </small>
                              ) : null}
                            </div>
                          </div>
                          <div className="col-md-4">
                            <div className={styles.form_group}>
                              <label>
                                Select Purpose of Appointment Booking
                              </label>
                              <select
                                className={styles.custom_select}
                                name="appointment_purpose"
                                onChange={(e) =>
                                  handleChange(
                                    "appointment_purpose",
                                    e.target.value
                                  )
                                }
                                value={formik.values.appointment_purpose}
                              >
                                {store &&
                                  store.purposes &&
                                  store.purposes.length > 0 && (
                                    <>
                                      <option selected>
                                        --Select Purpose of Appointment
                                        Booking--
                                      </option>
                                      {store.purposes.map(
                                        (value: any, index: number) => {
                                          return (
                                            <option
                                              key={index}
                                              value={value.id}
                                            >
                                              {value.title}
                                            </option>
                                          );
                                        }
                                      )}
                                    </>
                                  )}
                              </select>
                              {formik.touched.appointment_purpose &&
                              formik.errors.appointment_purpose ? (
                                <small className="text-danger">
                                  {formik.errors.appointment_purpose}
                                </small>
                              ) : null}
                            </div>
                          </div>
                          {subPurpose.length > 0 ? (
                            <div className="col-md-6" id="sub_purpose">
                              <div className={styles.form_group}>
                                <label>Select {subPurposeTitle} Purpose</label>
                                <select
                                  className={styles.custom_select}
                                  {...formik.getFieldProps("sub_purpose")}
                                >
                                  <option selected>
                                    --Select {subPurposeTitle} Purpose--
                                  </option>
                                  {subPurpose.length > 0 &&
                                    subPurpose.map(
                                      (value: any, index: number) => {
                                        return (
                                          <option key={index} value={value.id}>
                                            {value.title}
                                          </option>
                                        );
                                      }
                                    )}
                                </select>
                              </div>
                            </div>
                          ) : tncUrl ? (
                            <div className="col-md-12" id="purpose_tnc">
                              <input
                                type="checkbox"
                                style={{ marginRight: "5px" }}
                                id="purpose_tnc_accept"
                                checked={formik.values.accept_tc}
                                onChange={(e) =>
                                  handleChange("accept_tc", e.target.checked)
                                }
                              />
                              <label htmlFor="purpose_tnc_accept">
                                Accept to our{" "}
                                <a
                                  href={tncUrl ? tncUrl : ""}
                                  target="_blank"
                                  rel="noreferrer"
                                  style={{ color: "blue" }}
                                >
                                  Terms and Conditions
                                </a>{" "}
                                for Jewellery shopping through video call
                              </label>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  )}
                  {!authState.user && (
                    <div
                      className="row"
                      style={{
                        color: "#000",
                        marginBottom: "-25px",
                        marginTop: "10px",
                      }}
                    >
                      <p>
                        Please{" "}
                        <span
                          style={{ color: "#FF3233", cursor: "pointer" }}
                          onClick={handleLoginClick}
                        >
                          Login
                        </span>{" "}
                        to continue
                      </p>
                    </div>
                  )}
                  <div className="row pt-2">
                    <div className="col-md-4">
                      <div className={styles.form_group}>
                        <label>Name</label>
                        <div className={styles.input_group}>
                          <input
                            type="text"
                            className={styles.form_control}
                            name="name"
                            style={{
                              opacity: "0.8",
                              backgroundColor: "#e6e7e8",
                              color: "#000000",
                              cursor: "not-allowed",
                            }}
                            value={
                              authState && authState.user && authState.user.name
                                ? authState.user.name
                                : ""
                            }
                            disabled
                          />
                        </div>
                      </div>
                    </div>

                    <div className="col-md-4">
                      <div className={styles.form_group}>
                        <label>Mobile No.</label>
                        <input
                          type="tel"
                          className="form-control"
                          name="mobile_no"
                          style={{
                            opacity: "0.8",
                            backgroundColor: "#e6e7e8",
                            color: "#000000",
                            cursor: "not-allowed",
                          }}
                          value={
                            authState && authState.user && authState.user.mobile
                              ? authState.user.mobile
                              : ""
                          }
                          disabled
                        />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="col-md-12">
                        <div className={styles.form_group}>
                          <label>Email</label>
                          <input
                            type="email"
                            className={styles.form_control}
                            style={
                              authState &&
                              authState.user &&
                              authState.user.email
                                ? {
                                    opacity: "0.8",
                                    backgroundColor: "#e6e7e8",
                                    color: "#000000",
                                    cursor: "not-allowed",
                                  }
                                : {}
                            }
                            onChange={(e) =>
                              formik.setFieldValue("email", e.target.value)
                            }
                            value={
                              authState &&
                              authState.user &&
                              authState.user.email
                                ? authState.user.email
                                : formik.values.email
                            }
                            disabled={
                              authState &&
                              authState.user &&
                              authState.user.email
                                ? true
                                : false
                            }
                          />
                          {formik.touched.email && formik.errors.email ? (
                            <small className="text-danger">
                              {formik.errors.email}
                            </small>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row justify-content-center">
                    <div className="col-md-12 pt-1">
                      <button
                        type="button"
                        disabled={loadings}
                        onClick={formik.submitForm}
                        className={styles.buynow}
                      >
                        BOOK{" "}
                        {loadings && <Spinner animation="border" size="sm" />}
                      </button>
                    </div>
                    <div className="col-md-12 text-center">
                      <p id="errorDiv"></p>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </form>
    </Layout>
  );
};

export const getServerSideProps = wrapper.getServerSideProps(
  (store) =>
    async ({ resolvedUrl }) => {
      store.dispatch(setPath(resolvedUrl));
      store.dispatch(
        setTitle("Book a Jewellery Shopping Appointment | Senco Gold")
      );
      store.dispatch(
        setMeta({
          title: "Book a Jewellery Shopping Appointment | Senco Gold",
          description:
            "Discover elegance at its finest with Senco Gold's best jewellery store near you. Explore exquisite designs crafted for every occasion.",
          keywords: "Book a Jewellery Shopping Appointment | Senco Gold",
        })
      );

      return {
        props: {},
      };
    }
);

export default Bookanappointment;
